import React from 'react';
import moment from 'moment';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, Query } from 'react-apollo';
import { Formik, Field, FieldArray } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Drawer from '@material-ui/core/Drawer';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import TrashIcon from '@material-ui/icons/Delete';
import Button from 'components/common/Button';
import TextField from 'components/form/Formik/FormikInput';
import Select from 'components/form/Formik/FormikReactSelect';
import DateInput from 'components/form/Formik/FormikDateInput';
import Spin from 'components/common/Spin';
import useSnack from 'hooks/useSnack';
import { getFullName } from 'utils';
import {
  INSERT_PAYMENT_ITEM,
  UPDATE_PAYMENT_ITEM,
  GTE_USER_RATES,
} from 'graphql/payments';
import { GET_LC_STUDENTS } from 'graphql/lc/students';
import { GET_HC_STUDENTS } from 'graphql/hc/students';
import { GET_FA_STUDENTS } from 'graphql/fa/students';
import { GET_GROUPS } from 'graphql/groups';

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 600,
    padding: 20,
  },
  close: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
  card: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));

const STUDENTSQUERY = {
  lead_coach: GET_LC_STUDENTS,
  head_coach: GET_HC_STUDENTS,
  fa_coach: GET_FA_STUDENTS,
};

export const calcAmount = (item) => {
  const { duration, category } = item;
  if (category) {
    const { rate, rateType } = category;
    if (rateType === 'fixed') return rate / 100;
    if (!duration) return 0;
    const amount = ((rate / 60) * duration) / 100;
    return amount.toFixed(2);
  }
  return 0;
};

const LogTime = ({ onClose, onDone, item }) => {
  const classes = useStyles();
  const snack = useSnack();
  const user = useSelector((s) => s.user.user);
  const studentsQueryResponse = useQuery(STUDENTSQUERY[user.type], {
    variables: { user_id: user.id },
  });
  const groupsQueryResponse = useQuery(GET_GROUPS);

  const [insertItems] = useMutation(INSERT_PAYMENT_ITEM);
  const [updateItem] = useMutation(UPDATE_PAYMENT_ITEM);

  const students = studentsQueryResponse.data
    ? studentsQueryResponse.data.students.map((u) => ({
        value: u.user_id,
        label: getFullName(u),
        field: 'student_id'
      }))
    : [];

  const groups = groupsQueryResponse.data ?  groupsQueryResponse.data.groups.map(g => ({
    value: g.id,
    label: g.name,
    field: 'group_id'
  })) : [];

  const onSave = async (values, actions) => {
    const data = [];
    const subjectData = values.subject?.value ? {[values.subject.field]: values.subject.value } : {};
    values.items.forEach(i => {
      data.push({
        user_id: user.id,
        date: moment(values.date).format('YYYY-MM-DD'),
        payment_category_id: i.category.value,
        comments: i.comments,
        duration: i.duration,
        total: parseInt(Number(calcAmount(i)) * 100),
        ...subjectData
      })
    });
    try {
      if (item) {
        await updateItem({
          variables: {
            id: item.id,
            data: data[0]
          }
        });
        snack.success('Time log updated');
      } else {
        await insertItems({
          variables: { data }
        });
        snack.success('Time log added');
      }
      onDone();
      onClose();
    } catch (e) {
      snack.error(e.message);
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Drawer open anchor="right" classes={{ paper: classes.drawer }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={3}
      >
        <Typography variant="h5">Log Time</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Query query={GTE_USER_RATES} variables={{ userId: user.id }}>
        {({ data, loading, error }) => {
          if (loading) return <Spin spinning />;
          if (error) return error.message;

          if (!data.rows.length)
            return 'Your account is not configured. Please contact admin.';

          const { rows } = data;

          const non_client_facing_categories = rows.filter(
            (i) => i.payment_category.type === 'ncf',
          );
          const client_facing_categories = rows.filter(
            (i) => i.payment_category.type === 'cf',
          );
          const misc_categories = rows.filter(
            (i) => i.payment_category.type === 'misc',
          );
          const bonus_categories = rows.filter(
            (i) => i.payment_category.type === 'cb',
          );

          const options = [
            {
              label: 'Non client facing',
              options: non_client_facing_categories.map((i) => ({
                ...i.payment_category,
                rate: i.rate,
                rateType: i.rate_type,
              })),
            },
            {
              label: 'Client facing',
              options: client_facing_categories.map((i) => ({
                ...i.payment_category,
                rate: i.rate,
                rateType: i.rate_type,
              })),
            },
            {
              label: 'Completion bonus',
              options: bonus_categories.map((i) => ({
                label: i.payment_category.label,
                value: i.payment_category.id,
                rate: i.rate,
                rateType: i.rate_type,
              })),
            },
            {
              label: 'Misc',
              options: misc_categories.map((i) => ({
                label: i.payment_category.label,
                value: i.payment_category.id,
                rate: i.rate,
                rateType: i.rate_type,
              })),
            },
          ];

          const getExistingCategory = () => {
            const paymentCategory = rows.find(i => i.payment_category.id === item.payment_category_id);
            if (paymentCategory) {
              return {
                ...paymentCategory.payment_category,
                rate: paymentCategory.rate,
                rateType: paymentCategory.rate_type,
              }
            }
            return {

            }
          }

          return (
            <Formik
              initialValues={item ? {
                subject: item.group ? {
                  label: item.group.name,
                  value: item.group.id,
                  field: 'group_id'
                } : item.student ? {
                  label: getFullName(item.student),
                  value: item.student.user_id,
                  field: 'student_id'
                } : null,
                date: moment(item.date),
                items: [{
                  category: getExistingCategory(),
                  duration: item.duration,
                  comments: item.comments,
                }]
              } : {
                items: [{}]
              }}
              onSubmit={onSave}
            >
              {({ values, handleSubmit, isSubmitting }) => {
                return (
                  <Box>
                    <Box marginBottom={3}>
                      <Grid container spacing={2}>
                        <Grid item md={6}>
                          <Field
                            component={Select}
                            name="subject"
                            isMulti={false}
                            options={[{
                              label: 'Students',
                              options: students
                            }, {
                              label: 'Groups',
                              options: groups
                            }]}
                            placeholder="Select Student/Group"
                          />
                        </Grid>
                        <Grid item md={6}>
                          <Field
                            name="date"
                            component={DateInput}
                            fullWidth
                            format="L"
                            placeholder="Select Date"
                            inputVariant="outlined"
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <FieldArray name="items">
                      {({ push, remove }) => {
                        return values.items.map((i, idx) => (
                          <Card className={classes.card}>
                            <Box>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item md={5}>
                                  <Field
                                    noMargin
                                    label="Category"
                                    component={Select}
                                    name={`items.${idx}.category`}
                                    isMulti={false}
                                    options={options}
                                  />
                                </Grid>
                                <Grid item md={2}>
                                  <Typography variant="body2">Rate:</Typography>
                                  {i.category?.rateType
                                    ? i.category.rateType === 'hourly'
                                      ? `$${i.category.rate / 100}/hour`
                                      : `$${i.category.rate / 100}`
                                    : null}
                                </Grid>
                                <Grid item md={3}>
                                  <Field
                                    disableMargin
                                    label="Duration"
                                    component={TextField}
                                    name={`items.${idx}.duration`}
                                    type="number"
                                    placeholder="30 minutes"
                                  />
                                </Grid>
                                <Grid item md={2}>
                                  <Typography variant="body2">Total:</Typography>
                                  ${calcAmount(i)}
                                </Grid>
                              </Grid>
                              <Box marginTop={2} marginBottom={1}>
                                <Field
                                  component={TextField}
                                  multiline
                                  fullWidth
                                  rows={1}
                                  name={`items.${idx}.comments`}
                                  placeholder="Remarks..."
                                />
                              </Box>
                            </Box>
                            { !item && (
                              <>
                                {values.items.length > 1 && (
                                  <IconButton
                                    color="secondary"
                                    onClick={() => remove(idx)}
                                  >
                                    <TrashIcon />
                                  </IconButton>
                                )}
                                {values.items.length - 1 === idx && (
                                  <Button color="primary" onClick={() => push({})}>
                                    Add Item
                                  </Button>
                                )}
                              </>
                            )}
                          </Card>
                        ));
                      }}
                    </FieldArray>
                    <Box marginTop={3}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        loading={isSubmitting}
                      >
                        Submit
                      </Button>
                    </Box>
                  </Box>
                );
              }}
            </Formik>
          );
        }}
      </Query>
    </Drawer>
  );
};

export default LogTime;
